/* =============================================================
   dd-countdown-boot.js

   Eager initial-value boot for the "Order within H hours and M
   minutes…" countdown slots used by:
     • PDP urgency block (sections/main-product.liquid `dd_urgency`)
     • Header announcement-bar countdown block (rendered via
       snippets/announcement-bar-message.liquid)

   Design split:
     This file owns FIRST-PAINT initial values only. It's loaded with
     `async` from <head> in layout/theme.liquid — downloads in
     parallel with HTML parsing and runs as soon as the bytes arrive,
     without waiting for the deferred queue. As soon as it executes
     it walks every `[data-dd-countdown]` root, computes time-to-
     cutoff in the configured IANA timezone via
     `Intl.DateTimeFormat`, writes the result into the
     `[data-dd-countdown-hours]` / `[data-dd-countdown-minutes]`
     slots, marks the root with `data-dd-countdown-ready`, and exits.

     `assets/dd-countdown.js` (deferred) takes over after that for
     the long-running concerns:
       - 60s setInterval ticker (live updates while shopper lingers)
       - MutationObserver for cloned ticker markup in the glass-nav
       - Variant-change re-tick on the PDP urgency block

   Why this split?
     Init compute is latency-critical — must land before first paint
     settles, otherwise users see `--` placeholders. The companion
     `dd-countdown.js` is `defer`ed and on a long PDP doesn't execute
     until the entire HTML body finishes parsing (1–3s on slow mobile
     per the comments in main-product.liquid prior to this refactor).
     The previous "pre-compute in Liquid" workaround only fired when
     `shop.iana_timezone == cutoff_tz`, which almost never held in
     practice — so `--` was the typical first paint.

     This file moves the compute itself into the eager <head> path:
     a tiny IIFE (no observer, no interval, no listeners beyond DCL)
     so it downloads + runs without queuing behind every other
     deferred theme script. The big script stays deferred.

   Async + readyState dance:
     The IIFE may execute mid-parse (if `async` lands before body
     finishes parsing) OR post-parse (slow network, big body). We
     handle both by checking `document.readyState`: if `loading`,
     register a DCL listener; otherwise call `bootAll()` directly.
     Either way, the slots are populated by DCL at the latest.

   CSS contract:
     The companion stylesheet rule lives inline at the bottom of this
     file (injected once via a <style> tag) — it co-locates the
     "hide the wrapper while we're waiting" affordance with the script
     that flips it. Fallback: every code path here sets
     `data-dd-countdown-ready` on the root (success OR bail), so the
     wrapper always becomes visible eventually. If a browser has no
     `Intl.DateTimeFormat`, the slot stays at its server-rendered
     `--` and the deferred script will overwrite it on its first
     interval tick (~60s).

   Idempotent: re-loading is a no-op (`window._ddCountdownBootInit`).
   Manual invocation: `window.ddCountdownBoot()` (exposed for inline
   `<script>` calls right after a slot in markup if true first-paint
   timing is ever needed; not used by default).
   ============================================================= */
(function () {
  'use strict';
  if (window._ddCountdownBootInit) return;
  window._ddCountdownBootInit = true;

  /* Inject the companion visibility rule once. Keeps the CSS
     co-located with the JS that controls it; the rule depends
     entirely on the `data-dd-countdown-ready` attribute this file
     manages, so splitting it across files would invite drift. */
  function injectStyle() {
    if (document.getElementById('dd-countdown-boot-style')) return;
    var style = document.createElement('style');
    style.id = 'dd-countdown-boot-style';
    style.textContent =
      /* Hide the countdown wrapper until boot (or the deferred
         ticker) has populated and marked it `ready`. `visibility:
         hidden` keeps the wrapper's box in layout so the surrounding
         text doesn't reflow when the numbers appear — critical for
         the announcement-bar marquee where every glyph's x-position
         is load-bearing for the scroll loop math. */
      '[data-dd-countdown]:not([data-dd-countdown-ready]) { visibility: hidden; }';
    (document.head || document.documentElement).appendChild(style);
  }

  function getNextCutoffMs(cutoffHour, cutoffMinute, tz) {
    var cal = window.DDShippingCalendar;
    if (cal && typeof cal.getMsUntilNextCutoff === 'function') {
      return cal.getMsUntilNextCutoff(cutoffHour, cutoffMinute, tz);
    }
    return NaN;
  }

  function markReady(root) {
    if (!root) return;
    root.setAttribute('data-dd-countdown-ready', '');
  }

  function bootOne(root) {
    if (!root || root._ddCountdownBooted) return;
    var cutoffHour = parseInt(root.getAttribute('data-cutoff-hour'), 10);
    var cutoffMinute = parseInt(root.getAttribute('data-cutoff-minute'), 10) || 0;
    var tz = root.getAttribute('data-cutoff-tz') || 'America/Phoenix';
    /* Bail OPENLY (mark ready so the wrapper still reveals) if
       config is missing or timezone is unparseable. The slot
       stays at its server-rendered `--` and the deferred ticker
       will fix it on the next tick — but at least the surrounding
       message text isn't held invisible on a misconfigured block. */
    if (isNaN(cutoffHour)) { markReady(root); return; }

    var ms = getNextCutoffMs(cutoffHour, cutoffMinute, tz);
    if (isNaN(ms) || ms < 0) { markReady(root); return; }

    var totalMin = Math.floor(ms / 60000);
    var h = String(Math.floor(totalMin / 60));
    var m = String(totalMin % 60);

    /* Scope queries to THIS root only — multi-instance pages (PDP
       + header ticker) can have different cutoff configs. Skipping
       no-op writes mirrors dd-countdown.js for the same reason
       (mutation-storm safety against the ticker's MutationObserver). */
    var hoursEls = root.querySelectorAll('[data-dd-countdown-hours]');
    var minsEls = root.querySelectorAll('[data-dd-countdown-minutes]');
    for (var i = 0; i < hoursEls.length; i++) {
      if (hoursEls[i].textContent !== h) hoursEls[i].textContent = h;
    }
    for (var j = 0; j < minsEls.length; j++) {
      if (minsEls[j].textContent !== m) minsEls[j].textContent = m;
    }
    markReady(root);
    root._ddCountdownBooted = true;
  }

  function bootAll() {
    var roots = document.querySelectorAll('[data-dd-countdown]');
    for (var i = 0; i < roots.length; i++) bootOne(roots[i]);
  }

  injectStyle();

  /* Expose the manual entry-point so future render sites can call
     `window.ddCountdownBoot()` from an inline <script> tag right
     after their slot if they want zero-DCL-wait timing. The PDP and
     announcement-bar flow uses the DCL listener below — same wall-
     clock timing as `defer` would have given, but the boot work
     skips the long defer queue (lighter file, runs at the front of
     the DCL handler list). */
  window.ddCountdownBoot = bootAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
